import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { switchMap, map, catchError, of, withLatestFrom } from "rxjs";
import { QuoteClient } from "src/api-service";
import { selectUserContext } from "../../../../user/user.selectors";
import { normalizeError } from "../../../../app-store-error.types";
import { QuoteSearchActions } from "./quote-search.actions";

@Injectable()
export class QuoteSearchEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly quoteClient = inject(QuoteClient);

    searchQuotes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteSearchActions.searchQuotes),
            withLatestFrom(this.store.select(selectUserContext)),
            switchMap(([{ request }, user]) => {
                const companyId = user.selectedCompany?.id;
                if (!companyId) {
                    return of(QuoteSearchActions.searchQuotesFailure({
                        error: { message: "No company selected" },
                        toastKey: "ADMIN.SUPER_USER.QUOTE_SEARCH.TOAST.LOAD_ERROR",
                    }));
                }

                const allCompanyIds = user.companies.map(c => c.id);
                return this.quoteClient
                    .getFilteredFromMultipleCompanies(
                        {
                            pageNumber: request.pageNumber ?? 1,
                            pageSize: request.pageSize ?? 50,
                            filter: request.filter,
                            sortProperty: request.sortProperty ?? "displayId",
                            sortAscending: request.sortAscending ?? false,
                            companyIds: request.companyIds?.length ? request.companyIds : allCompanyIds,
                            statuses: request.statuses?.length ? request.statuses : [],
                            toCentralSalesSupport: false,
                            customerCode: request.customerCode,
                            salesPersonCode: request.salesPersonCode,
                            salesSupportPersonCode: request.salesSupportPersonCode,
                            locationCode: request.locationCode,
                            createdFrom: request.createdFrom,
                            createdTo: request.createdTo,
                            validToFrom: request.validToFrom,
                            validToTo: request.validToTo,
                        },
                        companyId,
                    )
                    .pipe(
                        map(response => QuoteSearchActions.searchQuotesSuccess({
                            quotes: response.data ?? [],
                            pagination: response.pagination,
                        })),
                        catchError(error =>
                            of(QuoteSearchActions.searchQuotesFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.QUOTE_SEARCH.TOAST.LOAD_ERROR",
                            })),
                        ),
                    );
            }),
        ),
    );
}
